/**
 * Pure helper for restoring per-message reaction state from
 * `GET /api/conversations/{id}/feedback` (lib/api/feedback.ts) — factored
 * out of page.tsx so the "which row wins for a given message" rule is
 * unit-tested without mounting the page or mocking fetch at the component
 * level.
 */
import { FeedbackScope, type FeedbackResponse } from '@/lib/api/feedback';

/**
 * Build a `message_id -> FeedbackResponse` map from the flat list the GET
 * endpoint returns. Only MESSAGE-scoped rows with a `message_id` count — a
 * CONVERSATION-scoped rating has no per-message row to restore into.
 *
 * When more than one row exists for the same message (a thumb, then a later
 * enrichment with reasons/comment via the reason panel's Send — see
 * message-reaction-row.tsx), the LAST one in array order wins: the backend
 * is expected to return rows in creation order, so this reduces to "most
 * recent state for this message."
 */
export function buildMessageFeedbackMap(
  responses: FeedbackResponse[]
): Record<string, FeedbackResponse> {
  const map: Record<string, FeedbackResponse> = {};
  for (const response of responses) {
    if (response.scope !== FeedbackScope.MESSAGE || !response.message_id) {
      continue;
    }
    map[response.message_id] = response;
  }
  return map;
}
